<template>
  <v-container>
    <div class="d-flex align-center justify-space-between mb-4">
      <h2 class="mb-0 text-truncate">Oracon - Orienteering Radio Control</h2>
      <div class="d-flex ga-2">
        <v-btn :loading="saving" color="success" @click="saveChanges">{{ $t('general.save_changes') }}</v-btn>
      </div>
    </div>

    <v-data-table
      :headers="headers"
      :items="localRows"
      :loading="loading"
      density="comfortable"
      item-key="id"
      class="elevation-2"
      :items-per-page="-1"
      fixed-header
      hover
      :row-props="setEditedRowProps"
      hide-default-footer
      no-data-text="radiocontrols.no_data"
    >

    <template #item.id="{ item }">
      <span class="text-no-wrap">Oracon {{ item.id }}</span>
    </template>

      <template v-if="isRCM" #item.club_id="{ item }">
        <v-select
          v-model="item.club_id"
          :items="clubOptions"
          item-title="title"
          item-value="id"
          density="compact"
          hide-details
          clearable
          style="min-width: 140px"
          @update:model-value="markEdited(item)"          
        />
      </template>

      <template #item.race_id="{ item }">
        <v-select
          v-model="item.race_id"
          :items="raceOptions"
          item-title="title"
          item-value="id"
          density="compact"
          hide-details
          clearable
          style="min-width: 250px"
          @update:model-value="markEdited(item)"
        />
      </template>

      <template #item.control_name="{ item }">
        <v-text-field
          v-model="item.control_name"
          density="compact"
          hide-details
          @update:model-value="markEdited(item)"
          style="min-width: 80px"          
        />
      </template>

      <template #item.device_key="{ item }">
        {{ item.device_key }}
      </template>
      <template #item.battery="{ item }">
        <div class="text-right">{{ item.battery }}%</div>
      </template>
      <template #item.signal="{ item }">
        <div class="text-right">{{ item.signal }}%</div>
      </template>
      <template #item.timestamp="{ item }">
        <span class="text-no-wrap">{{ $formatDateTime(item.timestamp) }}</span>
      </template>

    </v-data-table>



  <!-- Tabulka s PUNCHES -->
  <div class="d-flex align-center justify-space-between mb-4 mt-8">
    <div class="d-flex ga-2">
      <v-btn color="primary" :loading="loading" @click="fetchAll">{{ $t('general.refresh') }}</v-btn>
    </div>
  </div>

  <v-data-table
    :headers="punchHeaders"
    :items="punchRows"
    :loading="punchLoading"
    item-key="id"
    density="comfortable"
    class="elevation-2"
    :items-per-page="100"
    fixed-header
    hover
    :row-props="setPunchRowProps"
  >

    <template #item.radio_control_id="{ item }">
      <span class="text-no-wrap">Oracon {{ item.radio_control_id }}</span>
    </template>

    <template #item.si_number="{ item }">
      <v-text-field
        v-model.number="item.si_number"
        type="number"
        density="compact"
        hide-details
        @update:model-value="markPunchEdited(item)"
        style="min-width: 110px"
      />
    </template>

    <template #item.control_code="{ item }">
      <v-text-field
        v-model="item.control_code"
        density="compact"
        hide-details
        @update:model-value="markPunchEdited(item)"
        style="min-width: 70px"
      />
    </template>

    <template #item.control_name="{ item }">
      <v-text-field
        v-model="item.control_name"
        density="compact"
        hide-details
        @update:model-value="markPunchEdited(item)"
        style="min-width: 90px"
      />
    </template>

    <template #item.race_id="{ item }">
      <v-select
        v-model="item.race_id"
        :items="punchRaceOptions"
        item-title="title"
        item-value="id"
        density="compact"
        hide-details
        clearable
        @update:model-value="markPunchEdited(item)"
        style="min-width: 260px"
      />
    </template>

    <!-- readonly sloupce -->
    <template #item.radio_control_name="{ item }">
      <span class="text-no-wrap">{{ item.radio_control_name }}</span>
    </template>
    <template #item.punch_time="{ item }">
      <span class="text-no-wrap">{{ item.punch_time ?? item.punch_time }}</span>
    </template>
    <template #item.status="{ item }">
      <v-chip size="small" :color="punchStatusColor(item.status)" label>{{ item.status }}</v-chip>
    </template>
    <template #item.message="{ item }">
      <span class="text-no-wrap">{{ item.message }}</span>
    </template>
    <template #item.timestamp="{ item }">
      <span class="text-no-wrap">{{ $formatDateTime?.(item.timestamp) ?? item.timestamp }}</span>
    </template>

    <template #item.actions="{ item }">
      <v-btn
        v-if="punchEditedIds.has(item.id)"
        :loading="punchRowSavingIds.has(item.id)"
        size="small"
        color="primary"
        @click="saveSinglePunch(item)"
      >
        {{ $t('general.send') }}
      </v-btn>
    </template>

  </v-data-table>

  </v-container>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import apiClient from '/api.js'
import { useSnackbarStore } from '@/stores/snackbarStore'
import { useAuthStore } from '/auth.js'
import { useI18n } from 'vue-i18n'
import { useDateTimeFormat } from '@/composables/useDateTimeFormat'

const { formatShortDate } = useDateTimeFormat()

const { t } = useI18n()
const snackbar = useSnackbarStore()
const loading = ref(false)
const saving = ref(false)

const auth = useAuthStore()
const isRCM = computed(() => auth.isRadioControlManager())

const deviceHeadersFull = [
  { title: 'ID', value: 'id', width: 40 },
  { title: t('radiocontrols.device.club'), value: 'club_id', width: 170 },
  { title: t('radiocontrols.device.control_name'), value: 'control_name', width: 100 },
  { title: t('radiocontrols.device.race'), value: 'race_id' },
  { title: t('radiocontrols.device.device_key'), value: 'device_key', width: 280 },
  { title: t('radiocontrols.device.battery'), value: 'battery', align: 'end', width: 70 },
  { title: t('radiocontrols.device.signal'), value: 'signal', align: 'end', width: 70 },
  { title: t('radiocontrols.device.timestamp'), value: 'timestamp', width: 150 },
]

// dynamické hlavičky podle oprávnění:
const headers = computed(() => {
  if (isRCM.value) return deviceHeadersFull
  // skryj "Oddíl" a "Klíč zařízení"
  return deviceHeadersFull.filter(h => !['club_id','device_key'].includes(h.value))
})


const localRows = ref([])
const editedIds = ref(new Set())
const clubOptions = ref([])
const raceOptions = ref([])

async function fetchAll() {
  loading.value = true
  punchLoading.value = true
  try {
    const { data } = await apiClient.get('/radio/')
    // devices -> první tabulka
    localRows.value = (data.devices || []).map(r => ({ ...r }))
    // punches -> druhá tabulka
    punchRows.value = (data.punches || []).map(p => ({ ...p }))
    // options
    clubOptions.value = data.clubs || []
    const racesRaw = data.races || []
    const racesWithDateLabel = racesRaw.map((r) => {
      const base = r.title || ''
      const datePrefix =
        r.race_date != null && r.race_date !== ''
          ? `${formatShortDate(r.race_date)} `
          : ''
      return { id: r.id, title: `${datePrefix}${base}`.trim() }
    })
    raceOptions.value = racesWithDateLabel
    punchRaceOptions.value = racesWithDateLabel
    // reset dirty setů
    editedIds.value = new Set()
    punchEditedIds.value = new Set()
  } catch (e) {
    snackbar.error('Načtení dat selhalo.')
  } finally {
    loading.value = false
    punchLoading.value = false
  }
}


function markEdited(item) {
  // ať je to reaktivní
  editedIds.value = new Set([...editedIds.value, item.id])
}

const setEditedRowProps = ({ item }) => {
  return editedIds.value.has(item.id) ? { class: 'bg-edited' } : {}
}


async function saveChanges() {
  const edited = localRows.value.filter(r => editedIds.value.has(r.id))
  if (!edited.length) { snackbar.success('Žádné změny k uložení.'); return }
  saving.value = true
  try {
    const payload = edited.map(r => {
      const base = {
        id: r.id,
        race_id: r.race_id ?? null,
        control_name: r.control_name ?? '',
      }
      if (isRCM.value) {
        base.club_id = r.club_id ?? null
      }
      return base
    })
    await apiClient.put('/radio/?type=device', payload)
    snackbar.success('Změny byly uloženy.')
    await fetchAll()
  } catch (e) {
    snackbar.error('Uložení selhalo.')
  } finally {
    saving.value = false
  }
}

// PUNCHES ////////////////////////////////


// ----- PUNCHES state (oddělené názvy) -----
const punchHeaders = [
  { title: t('radiocontrols.punches.radio_control_id'), value: 'radio_control_id', width: 50 },
  { title: t('radiocontrols.punches.si_number'), value: 'si_number', width: 130 },
  { title: t('radiocontrols.punches.control_code'), value: 'control_code', width: 50 },
  { title: t('radiocontrols.punches.control_name'), value: 'control_name', width: 50 },
  { title: t('radiocontrols.punches.race'), value: 'race_id' },
  { title: t('radiocontrols.punches.punch_time'), value: 'punch_time', width: 130 },
  { title: t('radiocontrols.punches.status'), value: 'status', width: 70 },
  { title: t('radiocontrols.punches.message'), value: 'message', width: 200 },
  { title: t('radiocontrols.punches.timestamp'), value: 'timestamp', width: 130 },
  { title: t('radiocontrols.punches.actions'), value: 'actions', width: 120 }, 
]

const punchRows = ref([])
const punchRaceOptions = ref([])
const punchEditedIds = ref(new Set())
const punchLoading = ref(false)

function punchStatusColor(s) {
  if (s === 'OK') return 'green'
  if (s === 'QUEUED') return 'amber'
  if (s === 'ERROR') return 'red'
  return 'grey'
}


function markPunchEdited(item) {
  punchEditedIds.value = new Set([...punchEditedIds.value, item.id]) // reaktivní
}

const setPunchRowProps = ({ item }) => {
  return punchEditedIds.value.has(item.id) ? { class: 'bg-edited' } : {}
}

const punchRowSavingIds = ref(new Set())


async function saveSinglePunch(item) {
  punchRowSavingIds.value = new Set([...punchRowSavingIds.value, item.id])
  try {
    const payload = {
      id: item.id,
      si_number: item.si_number,
      control_code: item.control_code ?? '',
      control_name: item.control_name ?? '',
      race_id: item.race_id ?? null,
    }
    await apiClient.put('/radio/?type=punch', payload)  
    const next = new Set(punchEditedIds.value); next.delete(item.id); punchEditedIds.value = next
    await fetchAll()
    snackbar.success('Oražení bylo uloženo a odesláno do výsledků.')
  } catch (e) {
    snackbar.error('Uložení oražení selhalo.')
  } finally {
    const next = new Set(punchRowSavingIds.value); next.delete(item.id); punchRowSavingIds.value = next
  }
}



onMounted(fetchAll)




</script>


<style scoped>

:deep(.bg-edited) {
  background-color: rgba(255, 165, 0, 0.22) !important;
}

  :deep() .v-table .v-table__wrapper > table > thead > tr > th:not(:last-child) {
    padding: 0 10px !important; 

  }
  :deep() .v-table .v-table__wrapper > table > tbody > tr > td:not(:last-child), .v-table .v-table__wrapper > table > tbody > tr > th:not(:last-child) { 
    padding: 0 10px !important; 

  }

</style>
